// src/pages/profiles/StudentProfile.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  AcademicCapIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  IdentificationIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { FireLoader } from '../../components/Loader';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
const STATIC_BASE_URL = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

const buildImageUrl = (path) => {
  if (!path) return '';
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  if (path.startsWith('/uploads/')) return `${STATIC_BASE_URL}${path}`;
  if (path.startsWith('uploads/')) return `${STATIC_BASE_URL}/${path}`;
  return `${STATIC_BASE_URL}/uploads/students/${path}`;
};

const formatDate = (value) => {
  if (!value) return 'Not provided';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not provided';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const valueOrFallback = (value, fallback = 'Not provided') =>
  value === undefined || value === null || String(value).trim() === ''
    ? fallback
    : value;

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex min-w-0 items-start gap-3 border-b border-gray-100 py-3.5 last:border-b-0">
    <div className="rounded-lg bg-gray-50 p-2 text-gray-600">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-semibold text-gray-800">
        {valueOrFallback(value)}
      </p>
    </div>
  </div>
);

const Section = ({ title, description, children }) => (
  <section className="rounded-2xl bg-white p-5 sm:p-6">
    <div className="mb-4">
      <h2 className="text-base font-bold text-gray-900 sm:text-lg">{title}</h2>
      {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
    </div>
    <div className="grid gap-3 sm:grid-cols-2">{children}</div>
  </section>
);

export default function StudentProfile() {
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageFailed, setImageFailed] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setError('Your signed-in account information is unavailable.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First resolve the signed-in user's student ID, then load the same
      // complete student record used by the admin StudentDetails page.
      const identityResponse = await apiClient.get(`/students/by-user/${user.id}`);
      const identity = identityResponse?.data?.data;
      if (!identityResponse?.data?.success || !identity?.id) {
        throw new Error('No student profile was found for your account.');
      }

      const detailsResponse = await apiClient.get(`/students/${identity.id}`);
      const completeProfile = detailsResponse?.data?.data || detailsResponse?.data;
      setStudent(completeProfile || identity);
      setImageFailed(false);
    } catch (fetchError) {
      console.error('Failed to load student profile:', fetchError);
      setError(
        fetchError.response?.data?.message ||
          fetchError.message ||
          'Your profile could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const details = useMemo(() => {
    if (!student) return null;

    const firstName = student.first_name || student.firstName || '';
    const middleName = student.middle_name || student.middleName || '';
    const lastName = student.last_name || student.lastName || '';
    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ') || 'Student';
    const initials = [firstName, lastName]
      .filter(Boolean)
      .map((name) => name.charAt(0).toUpperCase())
      .join('') || 'ST';
    const parent = student.parentUser || student.parent_user || student.parent || {};
    const address = [student.address, student.city, student.state, student.country]
      .filter(Boolean)
      .join(', ');
    const parentName =
      parent.full_name ||
      parent.fullName ||
      [
        parent.first_name || parent.firstName || student.parent_first_name,
        parent.last_name || parent.lastName || student.parent_last_name,
      ]
        .filter(Boolean)
        .join(' ');

    return {
      firstName,
      middleName,
      lastName,
      fullName,
      initials,
      parent,
      parentName,
      address,
      className: student.class?.name || student.Class?.name || student.class_name,
      academicYear:
        student.academicYear?.name || student.academic_year?.name || student.academic_year_name,
      parentEmail: parent.email || student.parent_email,
      parentPhone: parent.phone || student.parent_phone || student.emergency_contact_phone,
      parentAddress: parent.address || student.parent_address,
      parentOccupation: parent.occupation || student.parent_occupation,
      parentRelationship:
        student.parent_relationship ||
        student.parentRelationship ||
        student.emergency_contact_relationship,
      photo: buildImageUrl(
        student.profile_picture ||
          student.profilePicture ||
          student.user?.profile_picture ||
          user?.profile_picture
      ),
    };
  }, [student, user?.profile_picture]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <FireLoader />
      </div>
    );
  }

  if (error || !student || !details) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <UserCircleIcon className="mx-auto h-16 w-16 text-gray-300" />
        <h1 className="mt-4 text-xl font-bold text-gray-900">Profile unavailable</h1>
        <p className="mt-2 text-sm text-gray-500">{error}</p>
        <button
          type="button"
          onClick={fetchProfile}
          className="mt-5 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
        >
          Try again
        </button>
      </div>
    );
  }

  const status = String(student.status || (student.is_active ? 'active' : 'inactive'));

  return (
    <>
      <Helmet>
        <title>{details.fullName} | Student Profile</title>
      </Helmet>

      <div className="mx-auto max-w-6xl space-y-5">
        <section className="rounded-2xl bg-white px-5 py-7 sm:px-8 sm:py-8">
          <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
              <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100 text-3xl font-bold text-gray-600 sm:h-32 sm:w-32">
                {details.photo && !imageFailed ? (
                  <img
                    src={details.photo}
                    alt={details.fullName}
                    className="h-full w-full object-cover"
                    onError={() => setImageFailed(true)}
                  />
                ) : (
                  details.initials
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="break-words text-2xl font-bold text-gray-900 sm:text-3xl">
                  {details.fullName}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className="rounded-full bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                    {details.className || 'Class not assigned'}
                  </span>
                  <span className={`rounded-full px-3 py-1 font-semibold ${status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
          </div>
        </section>

        <div className="grid gap-5 lg:grid-cols-2">
          <Section title="Academic information" description="Your current school record and placement.">
            <InfoItem icon={IdentificationIcon} label="Admission Number" value={student.admission_number || student.admissionNumber} />
            <InfoItem icon={AcademicCapIcon} label="Current Class" value={details.className} />
            <InfoItem icon={CalendarDaysIcon} label="Academic Year" value={details.academicYear} />
            <InfoItem icon={CalendarDaysIcon} label="Enrolment Date" value={formatDate(student.enrollment_date || student.enrollmentDate)} />
            <InfoItem icon={CalendarDaysIcon} label="Graduation Date" value={formatDate(student.graduation_date || student.graduationDate)} />
          </Section>

          <Section title="Personal information" description="Your personal and identity details.">
            <InfoItem icon={UserCircleIcon} label="First Name" value={details.firstName} />
            <InfoItem icon={UserCircleIcon} label="Middle Name" value={details.middleName} />
            <InfoItem icon={UserCircleIcon} label="Surname" value={details.lastName} />
            <InfoItem icon={CalendarDaysIcon} label="Date of Birth" value={formatDate(student.date_of_birth || student.dateOfBirth)} />
            <InfoItem icon={UserCircleIcon} label="Gender" value={student.gender} />
            <InfoItem icon={ShieldCheckIcon} label="Nationality" value={student.nationality} />
            <InfoItem icon={ShieldCheckIcon} label="Religion" value={student.religion} />
          </Section>

          <Section title="Contact and address" description="How the school can contact and locate you.">
            <InfoItem icon={EnvelopeIcon} label="Email" value={student.email || student.user?.email || user?.email} />
            <InfoItem icon={PhoneIcon} label="Phone" value={student.phone} />
            <InfoItem icon={MapPinIcon} label="Address" value={details.address} />
            <InfoItem icon={MapPinIcon} label="City" value={student.city} />
          </Section>

          <Section title="Parent or guardian" description="Your linked parent and emergency contact information.">
            <InfoItem icon={UsersIcon} label="Parent/Guardian Name" value={details.parentName || student.parent_name} />
            <InfoItem icon={EnvelopeIcon} label="Parent Email" value={details.parentEmail} />
            <InfoItem icon={PhoneIcon} label="Parent Phone" value={details.parentPhone} />
            <InfoItem icon={UsersIcon} label="Relationship" value={details.parentRelationship} />
            <InfoItem icon={BriefcaseIcon} label="Occupation" value={details.parentOccupation} />
            <InfoItem icon={MapPinIcon} label="Parent Address" value={details.parentAddress} />
          </Section>
        </div>
      </div>
    </>
  );
}